import { useIsFocused } from '@react-navigation/core';
import { usePathname } from 'expo-router';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useRewardTierApplied } from '@fhs/loyalty/src/hooks/use-reward-tier-applied';
import useDebouncedEffect from '@fhs-legacy/frontend/src/hooks/use-debounce-effect';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import {
  actions,
  selectors,
  useAppDispatch,
  useAppSelector,
} from '@fhs-legacy/frontend/src/state/global-state';
import { useLoyaltyContext } from '@fhs-legacy/frontend/src/state/loyalty';
import { IAppliedRewards } from '@fhs-legacy/frontend/src/state/loyalty/hooks/types';
import { useLoyaltyUser } from '@fhs-legacy/frontend/src/state/loyalty/hooks/use-loyalty-user';
import { usePaymentContext } from '@fhs-legacy/frontend/src/state/payment';
import { mapPaymentMethodToLoyaltyPaymentMethods } from '@fhs-legacy/frontend/src/utils/payment';

import { useCartValues } from '../../../state/cart-store';

export function useLoyaltyIncentivesEvaluation() {
  /*
   * It handles both offers and rewards evaluation, dispatching the results to the Redux store whenever cart changes
   * the 250ms debounce prevents excessive API calls;
   * skipTimeValidation is set to false to ensure time-restricted offers are properly validated
   */
  const { cartEntries } = useCartValues();
  const { calculatedCartTotal } = useCartContext();
  const { loyaltyUserId } = useLoyaltyUser();
  const { checkoutPaymentMethod } = usePaymentContext();
  const dispatch = useAppDispatch();
  const { evaluateLoyaltyUserIncentives, evaluateLoyaltyUserRewards } = useLoyaltyContext();
  const { rewardTierApplied } = useRewardTierApplied();

  const { appliedLoyaltyDiscountOffer, incentiveSelections, appliedLoyaltyOffers, offersFeedbackMap } =
    useAppSelector(state => ({
      appliedLoyaltyDiscountOffer: selectors.loyalty.selectDiscountAppliedCmsOffer(state),
      incentiveSelections: selectors.loyalty.selectSelectedOfferSelections(state),
      appliedLoyaltyOffers: selectors.loyalty.selectAppliedOffers(state),
      offersFeedbackMap: selectors.loyalty.selectOfferFeedbackMap(state),
    }));

  const appliedRewards: IAppliedRewards = useMemo(() => {
    if (!rewardTierApplied || !incentiveSelections?.length) {
      return {};
    }

    const rewardSelections = incentiveSelections.filter(
      incentive => incentive.incentiveId === rewardTierApplied.id
    );

    return rewardSelections.reduce<IAppliedRewards>((acc, rewardSelection) => {
      acc[rewardSelection.lineId] = {
        timesApplied: 1,
        rewardId: rewardTierApplied.id,
        pointCost: rewardTierApplied.points ?? 0,
        rewardBenefitId: rewardTierApplied.id,
      };
      return acc;
    }, {});
  }, [rewardTierApplied, incentiveSelections]);

  const isFocused = useIsFocused();
  const isFocusedRef = useRef(isFocused);
  isFocusedRef.current = isFocused;

  const pathname = usePathname();
  const hasRunInitialEvaluation = useRef(false);
  const prevAppliedRewardsCount = useRef(0);
  const lastUnfocusedPathnameRef = useRef<string | null>(null);

  const runIncentivesEvaluation = useCallback(() => {
    if (!loyaltyUserId) {
      return;
    }
    const paymentMethod = mapPaymentMethodToLoyaltyPaymentMethods(checkoutPaymentMethod);

    if (appliedLoyaltyOffers.length) {
      evaluateLoyaltyUserIncentives({
        loyaltyId: loyaltyUserId,
        appliedLoyaltyOffers,
        cartEntries,
        appliedRewards,
        subtotalAmount: calculatedCartTotal,
        paymentMethod,
        appliedLoyaltyDiscountOffer,
        incentiveSelections,
        skipTimeValidation: false, // Enable time validation to show time-restricted offer messages
      });
    }

    if (isEmpty(appliedRewards)) {
      dispatch(actions.loyalty.resetAppliedLoyaltyRewardsFeedbackMap());
      return;
    }

    evaluateLoyaltyUserRewards({
      loyaltyId: loyaltyUserId,
      appliedLoyaltyRewards: appliedRewards,
      cartEntries,
      subtotalAmount: calculatedCartTotal,
      paymentMethod,
    });
  }, [
    loyaltyUserId,
    appliedLoyaltyOffers,
    cartEntries,
    appliedRewards,
    calculatedCartTotal,
    checkoutPaymentMethod,
    appliedLoyaltyDiscountOffer,
    incentiveSelections,
    evaluateLoyaltyUserIncentives,
    evaluateLoyaltyUserRewards,
    dispatch,
  ]);

  // Run evaluation immediately when conditions are met for the first time
  // This ensures time-restricted offer validation messages appear immediately on cart page load
  useEffect(() => {
    if (
      !hasRunInitialEvaluation.current &&
      loyaltyUserId &&
      appliedLoyaltyOffers.length > 0 &&
      cartEntries.length > 0
    ) {
      hasRunInitialEvaluation.current = true;
      runIncentivesEvaluation();
    }
  }, [loyaltyUserId, appliedLoyaltyOffers.length, cartEntries.length, runIncentivesEvaluation]);

  // Run evaluation immediately when user just applied a reward
  // Avoids 250ms window where banner could show "Reward Not Applied" before evaluation completes
  const appliedRewardsCount = Object.keys(appliedRewards).length;
  useEffect(() => {
    if (
      hasRunInitialEvaluation.current &&
      loyaltyUserId &&
      appliedRewardsCount > 0 &&
      prevAppliedRewardsCount.current === 0
    ) {
      runIncentivesEvaluation();
    }
    prevAppliedRewardsCount.current = appliedRewardsCount;
  }, [appliedRewardsCount, loyaltyUserId, runIncentivesEvaluation]);

  useEffect(() => {
    if (!isFocused) {
      lastUnfocusedPathnameRef.current = pathname;
      return;
    }

    const cameFromCheckout =
      lastUnfocusedPathnameRef.current?.includes('payment') ||
      lastUnfocusedPathnameRef.current?.includes('checkout');

    if (hasRunInitialEvaluation.current && cameFromCheckout && !isEmpty(offersFeedbackMap)) {
      runIncentivesEvaluation();
    }
  }, [isFocused, pathname, offersFeedbackMap, runIncentivesEvaluation]);

  // Debounced effect only run on on subsequent calls, needs immediate call from useEffect above
  useDebouncedEffect(
    () => {
      if (hasRunInitialEvaluation.current && isFocusedRef.current) {
        runIncentivesEvaluation();
      }
    },
    250,
    [
      loyaltyUserId,
      appliedLoyaltyOffers,
      cartEntries,
      appliedRewards,
      calculatedCartTotal,
      checkoutPaymentMethod,
      appliedLoyaltyDiscountOffer,
      incentiveSelections,
      evaluateLoyaltyUserIncentives,
      evaluateLoyaltyUserRewards,
      dispatch,
    ]
  );
}
